import { useEffect, useState } from 'react'
import { Layout } from './components/Layout'
import type { Page } from './components/Layout'
import { OverviewPage } from './pages/OverviewPage'
import { PipelinePage } from './pages/PipelinePage'
import { HealthPage } from './pages/HealthPage'
import { LandscapePage } from './pages/LandscapePage'
import { ClusterPage } from './pages/ClusterPage'
import { GitOpsPage } from './pages/GitOpsPage'
import { TroubleshootPage } from './pages/TroubleshootPage'
import { StatusPage } from './pages/StatusPage'
import { SecretsPage } from './pages/SecretsPage'
import { BackgroundJobsPage } from './pages/BackgroundJobsPage'
import { ReleasesPage } from './pages/ReleasesPage'
import { DevTokensPage } from './pages/DevTokensPage'
import { ImagesPage } from './pages/ImagesPage'
import { BranchingDiagramPage } from './pages/BranchingDiagramPage'

import { Boards } from './boards/Boards'

const SESSION_KEY = 'ops-active-page'

export function App() {
    const [page, setPage] = useState<Page>(() => {
        const saved = sessionStorage.getItem(SESSION_KEY) as Page | null
        return window.location.pathname.startsWith('/boards') ? 'boards' : saved === 'boards' ? 'overview' : saved ?? 'overview'
    })
    const [authChecked, setAuthChecked] = useState(false)

    function navigate(p: Page) {
        sessionStorage.setItem(SESSION_KEY, p)
        if (p === 'boards') {
            window.history.pushState({}, '', '/boards')
            window.dispatchEvent(new PopStateEvent('popstate'))
        }
        else if (window.location.pathname.startsWith('/boards')) window.history.pushState({}, '', '/')
        setPage(p)
    }

    useEffect(() => {
        const onPop = () => setPage(window.location.pathname.startsWith('/boards') ? 'boards' : 'overview')
        window.addEventListener('popstate', onPop)
        return () => window.removeEventListener('popstate', onPop)
    }, [])

    useEffect(() => {
        fetch('/api/me', { credentials: 'include' })
            .then(res => {
                if (res.status === 401) {
                    window.location.href = `/signin?returnUrl=${encodeURIComponent(window.location.pathname)}`
                } else {
                    setAuthChecked(true)
                }
            })
            // A network-level error (TypeError) here usually means the server
            // redirected the API call to the Entra login page, causing the
            // browser to follow a cross-origin redirect and throw a CORS error.
            // Treat any fetch failure on /api/me as unauthenticated and go to
            // /signin rather than rendering the app in a broken state.
            .catch(() => { window.location.href = `/signin?returnUrl=${encodeURIComponent(window.location.pathname)}` })
    }, [])

    if (!authChecked) return null

    return (
        <Layout page={page} onNavigate={navigate}>
            {page === 'boards' && <Boards />}
            {page === 'overview' && <OverviewPage onNavigate={navigate} />}
            {page === 'pipeline' && <PipelinePage />}
            {page === 'health' && <HealthPage />}
            {page === 'landscape' && <LandscapePage />}
            {page === 'cluster' && <ClusterPage />}
            {page === 'gitops' && <GitOpsPage />}
            {page === 'status' && <StatusPage />}
            {page === 'troubleshoot' && <TroubleshootPage />}
            {page === 'secrets' && <SecretsPage />}
            {page === 'jobs' && <BackgroundJobsPage />}
            {page === 'releases' && <ReleasesPage />}
            {page === 'tokens' && <DevTokensPage />}
            {page === 'images' && <ImagesPage />}
            {page === 'branches' && <BranchingDiagramPage />}
        </Layout>
    )
}
